import Immutable from 'immutable'

import type { CoreSlotId, OrderId } from 'actions/menu'
import type { PortionSizes } from 'config/portionSizes'
import type {
  BasketAddress,
  BasketOrderDetails,
  BasketQuantities,
  BasketState,
  RecipePositions,
  RecipeQuantities,
} from 'reducers/basket'
import type { MenuRecipesState, RecipeUUID } from 'reducers/menu-recipes/recipes'
import { getAvailableRecipes } from 'selectors/menu-recipes/getAvailableRecipes'
import { getBasketRecipeNumPortions, getBasketRecipeQuantity } from 'utils/basket'
import type { ImmutableList, ImmutableMap } from 'utils/types/immutable'

type DeliverySlot = ImmutableMap<{
  deliveryEndTime: string
  deliveryStartTime: string
  id: string
}>

type RootState = {
  basket: BasketState
  user: ImmutableMap<{
    shippingAddresses?: ImmutableList<BasketAddress>
  }>
  boxSummaryDeliveryDays: ImmutableMap<
    Record<
      string,
      ImmutableMap<{
        slots: ImmutableList<DeliverySlot>
      }>
    >
  >
} & MenuRecipesState

const emptyMap = Immutable.Map() as unknown as RecipeQuantities

export const getBasketOrderId = (state: Pick<RootState, 'basket'>): OrderId | '' | undefined =>
  state.basket.get('orderId')
export const isBasketTransactionalOrder = (state: Pick<RootState, 'basket'>) =>
  !getBasketOrderId(state)
export const getBasketRecipes = (state: Pick<RootState, 'basket'>): RecipeQuantities =>
  state.basket.get('recipes', emptyMap)
export const getBasketRecipeQuantityForId =
  (recipeId: RecipeUUID | null | undefined) =>
  (state: Pick<RootState, 'basket'>): number =>
    recipeId ? getBasketRecipeQuantity(getBasketRecipes(state).get(recipeId)) : 0
export const getBasketRecipePositions = ({ basket }: Pick<RootState, 'basket'>): RecipePositions =>
  basket.get('recipePositions')
export const getBasketRecipesCount = (state: Pick<RootState, 'basket'>): number =>
  state.basket
    .get('recipes', emptyMap)
    .reduce((sum, recipeEntry) => sum + getBasketRecipeQuantity(recipeEntry), 0)
export const getNumPortions = (state: Pick<RootState, 'basket'>): PortionSizes =>
  state.basket.get('numPortions')
export const getBasketRecipeNumPortionsForId =
  (recipeId: RecipeUUID | null | undefined) =>
  (state: Pick<RootState, 'basket'>): PortionSizes =>
    recipeId
      ? getBasketRecipeNumPortions(getBasketRecipes(state).get(recipeId), getNumPortions(state))
      : getNumPortions(state)
export const getPromoCode = (state: Pick<RootState, 'basket'>): string | false =>
  state.basket.get('promoCode', false)
export const getPreviewOrderId = (state: Pick<RootState, 'basket'>): OrderId | '' =>
  state.basket.get('previewOrderId')
export const getBasketOrderDetails = (
  state: Pick<RootState, 'basket'>,
): BasketOrderDetails | undefined | false => state.basket.get('orderDetails', false)
export const getBasketDate = (state: Pick<RootState, 'basket'>): string => state.basket.get('date')
export const getBasketSlotId = (state: Pick<RootState, 'basket'>): CoreSlotId | '' =>
  state.basket.get('slotId')
export const getBasketPostcode = (state: Pick<RootState, 'basket'>): string =>
  state.basket.get('postcode')
export const getBasketMenuId = (state: Pick<RootState, 'basket'>): string =>
  state.basket.get('currentMenuId')
export const getBasketMenuUuid = (state: Pick<RootState, 'basket'>): string =>
  state.basket.get('currentMenuUuid')
export const getBasketIsPortionSizeViewVisible = (state: Pick<RootState, 'basket'>): boolean =>
  state.basket.get('isPortionSizeViewVisible')
export const getBasketIsShowDetailsViewFirst = (state: Pick<RootState, 'basket'>): boolean =>
  state.basket.get('isShowDetailsViewFirst')

export const getBasketProducts = (state: Pick<RootState, 'basket'>): BasketQuantities =>
  state.basket.get('products')

export const shouldShowBoxSummary = (state: Pick<RootState, 'basket'>): boolean =>
  !getBasketSlotId(state) && !!getBasketPostcode(state)

export const getChosenAddressId = (state: Pick<RootState, 'basket'>): string | null =>
  state.basket.getIn(['chosenAddress', 'id'] as const, null) as string | null

export const getBasketSaveRequired = (state: Pick<RootState, 'basket'>): boolean =>
  state.basket.get('unsaved')

export const getChosenAddress = (
  state: Pick<RootState, 'basket' | 'user'>,
): BasketAddress | undefined => {
  const defaultAddress = state.user
    .get('shippingAddresses')
    ?.filter((address) => address.get('shippingDefault'))
    .first()
  const chosenAddress = state.basket.get('chosenAddress')

  return chosenAddress || defaultAddress
}

export const getBasketUsageCount = (state: Pick<RootState, 'basket' | 'recipesV3'>): number => {
  const availableRecipes = getAvailableRecipes(state)

  const basketRecipes = state.basket.get('recipes').toJS()

  let usage = 0

  const basketRecipeUuids = Object.keys(basketRecipes) as RecipeUUID[]
  basketRecipeUuids.forEach((recipeUUID) => {
    if (recipeUUID in availableRecipes) {
      usage += getBasketRecipeQuantity(basketRecipes[recipeUUID])
    }
  })

  return usage
}

export const getSelectedDeliverySlot = (
  state: Pick<RootState, 'basket' | 'boxSummaryDeliveryDays'>,
): DeliverySlot | null => {
  const date = state.basket.get('date')
  const slotId = state.basket.get('slotId')

  if (!date || !slotId) return null

  const slots = state.boxSummaryDeliveryDays.getIn([date, 'slots' as const], null)
  if (!slots) return null

  const selectedSlot = slots.find((slot) => slot.get('id') === slotId)

  return selectedSlot || null
}

export const getBasketIsFlexOrder = (state: Pick<RootState, 'basket'>): boolean =>
  state.basket.get('isFlexOrder', false)
